import { useMutation } from '@tanstack/react-query';
import {
  EmulateHeaders,
  type PublishAssessmentInput,
  gqlClient,
  graphql,
  queryClient,
  useAcademicNamespace,
} from '@tyro/api';
import { toast, useNumber } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import type { Dayjs } from 'dayjs';
import { getAssessmentSubjectGroups } from './assessment-subject-groups';
import { assessmentsKeys } from './keys';

const publishStateCbaOnline = graphql(/* GraphQL */ `
  mutation assessment_publishStateCba($input: PublishAssessmentInput) {
    assessment_publishStateCba(input: $input) {
      success
    }
  }
`);

const publishAssessmentOnline = graphql(/* GraphQL */ `
  mutation assessment_publish($input: PublishAssessmentInput) {
    assessment_publish(input: $input) {
      success
    }
  }
`);

export function usePublishStateCba(academicNameSpaceId?: number) {
  const { t } = useTranslation(['common']);
  const { activeAcademicNamespace } = useAcademicNamespace();

  return useMutation({
    mutationFn: (input: PublishAssessmentInput) =>
      gqlClient.request(
        publishStateCbaOnline,
        { input },
        {
          [EmulateHeaders.ACADEMIC_NAMESPACE_ID]: (
            academicNameSpaceId ??
            activeAcademicNamespace?.academicNamespaceId ??
            0
          )?.toString(),
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: assessmentsKeys.all });
    },
    onError: () => {
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
  });
}

export function usePublishAssessment() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: (input: PublishAssessmentInput) =>
      gqlClient.request(publishAssessmentOnline, { input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: assessmentsKeys.all });
    },
    onError: () => {
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
  });
}

async function getSubjectGroupIds(
  academicNamespaceId: number,
  assessmentId: number,
) {
  const response = await getAssessmentSubjectGroups(academicNamespaceId, {
    assessmentId,
  });

  const data = response?.assessment_assessmentSubjectGroups;
  const subjectGroupIds = data?.map(
    (subject) => subject?.subjectGroup?.partyId,
  );

  return subjectGroupIds;
}

export function usePublishAssessmentBasedOnType(
  id: number,
  isTermAssessment: boolean,
) {
  const { t } = useTranslation(['assessments', 'common']);

  const { mutateAsync: publishAssessment, isPending: isSubmitting } =
    usePublishAssessment();
  const { mutateAsync: publishStateCba, isPending: isSubmittingStateCba } =
    usePublishStateCba();

  const { activeAcademicNamespace } = useAcademicNamespace();
  const academicNamespaceIdAsNumber =
    useNumber(activeAcademicNamespace?.academicNamespaceId) ?? 0;

  return {
    publish: async (publishDate: Dayjs, onClose: () => void) => {
      if (isTermAssessment) {
        publishAssessment(
          {
            assessmentId: id,
            publish: true,
            publishFrom: publishDate.format('YYYY-MM-DD'),
          },
          {
            onSuccess: () => {
              onClose();
              toast.success(t('common:snackbarMessages.updateSuccess'));
            },
          },
        );
      } else {
        try {
          const subjectGroupIds = await getSubjectGroupIds(
            academicNamespaceIdAsNumber,
            id,
          );

          publishStateCba(
            {
              assessmentId: id,
              subjectGroupIds,
              publish: true,
              publishFrom: publishDate.format('YYYY-MM-DD'),
            },
            {
              onSuccess: () => {
                onClose();
                toast.success(t('common:snackbarMessages.updateSuccess'));
              },
            },
          );
        } catch (error) {
          console.error(error);
        }
      }
    },
    unpublish: async () => {
      if (isTermAssessment) {
        publishAssessment(
          {
            assessmentId: id,
            publish: false,
          },
          {
            onSuccess: () => {
              toast.success(t('assessments:unpublishedSuccessfully'));
            },
          },
        );
      } else {
        try {
          const subjectGroupIds = await getSubjectGroupIds(
            academicNamespaceIdAsNumber,
            id,
          );

          publishStateCba(
            {
              assessmentId: id,
              subjectGroupIds,
              publish: false,
            },
            {
              onSuccess: () => {
                toast.success(t('assessments:unpublishedSuccessfully'));
              },
            },
          );
        } catch (error) {
          console.error(error);
        }
      }
    },
    isSubmitting,
    isSubmittingStateCba,
  };
}
